import { useAvailablePeriods } from '@entities/available-periods';
import { useTz } from '@entities/schedules';
import { getScheduleServiceDuration } from '@entities/schedules/service/helpers.ts';
import type { ScheduleTerm } from '@entities/schedules/term/index.ts';
import { endOfDay } from 'date-fns';
import zonedTimeToUtc from 'date-fns-tz/zonedTimeToUtc';
import addMinutes from 'date-fns/addMinutes';
import addSeconds from 'date-fns/addSeconds';
import differenceInMinutes from 'date-fns/differenceInMinutes';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';
import subSeconds from 'date-fns/subSeconds';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useUser } from '@shared/hooks';
import type { AvailablePeriod } from '@shared/types/available-periods';

export const useMousePosition = () => {
  const [mousePosition, setMousePosition] = useState({ x: null, y: null });

  useEffect(() => {
    const updateMousePosition = (ev: any) => {
      setMousePosition({
        x: ev.clientX,
        y: ev.clientY,
      });
    };
    window.addEventListener('mousemove', updateMousePosition);
    return () => {
      window.removeEventListener('mousemove', updateMousePosition);
    };
  }, []);
  return mousePosition;
};

export const useSessionCreateOld = (mode: string) => {
  const { user } = useUser();
  const { availablePeriods } = useAvailablePeriods();
  const { tz } = useTz();

  const onlineSettingsFilled = user?.consultation_cost && user.consultation_duration;
  const offlineSettingsFilled =
    user?.consultation_offline_cost && user.consultation_offline_duration;

  const defaultParams = {
    x: 0,
    y: 0,
    start: new Date(),
    mode: [mode],
    duration: 1,
    isVisible: false,
    action: 'update',
    part: 'full',
  };

  const sessionRef = useRef<{
    x: number;
    y: number;
    start: Date;
    mode: string[];
    duration: number;
    isVisible: boolean;
    action: string;
    part: string;
  }>(defaultParams);

  const calendarRef = useRef<HTMLDivElement>(null);

  const getRealDuration = useCallback(() => {
    if (mode === 'ON')
      return user?.consultation_duration ? parseFloat(user.consultation_duration) : 0;

    if (mode === 'OF')
      return user?.consultation_offline_duration
        ? parseFloat(user.consultation_offline_duration)
        : 0;

    return 0;
  }, [mode, user?.consultation_duration, user?.consultation_offline_duration]);

  useEffect(() => {
    const updateParams = () => {
      const duration = getRealDuration();

      sessionRef.current = {
        ...sessionRef.current,
        duration: duration,
        mode: [mode],
      };
    };

    updateParams();
  }, [user, mode, getRealDuration]);

  const onEmptyHover = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    session: { start: Date; end: Date }
  ) => {
    if (!calendarRef.current) return;
    const realDuration = getRealDuration();

    if (mode === 'ON' && !onlineSettingsFilled) return;
    if (mode === 'OF' && !offlineSettingsFilled) return;

    const end = addMinutes(session.start, realDuration * 15);
    const isIntersecting = !!availablePeriods.find(
      (available) =>
        (parseISO(available.start_time) >= zonedTimeToUtc(session.start, tz) &&
          parseISO(available.end_time) <= zonedTimeToUtc(end, tz)) ||
        (parseISO(available.start_time) < zonedTimeToUtc(end, tz) &&
          zonedTimeToUtc(end, tz) <= parseISO(available.end_time))
    );

    const isOverlapping = session.start.getDate() < subSeconds(end, 1).getDate();

    const partialDuration =
      differenceInMinutes(addSeconds(endOfDay(subSeconds(session.end, 1)), 1), session.start) / 15;

    const coords = e.currentTarget.getBoundingClientRect();

    if (!isIntersecting)
      sessionRef.current = {
        ...sessionRef.current,
        start: session.start,
        mode: [mode],
        isVisible: true,
        action: 'create',
        duration: isOverlapping ? partialDuration : realDuration,
        part: isOverlapping ? 'start' : 'full',
        x: coords.x,
        y: coords.y,
      };

    if (isIntersecting)
      sessionRef.current = {
        ...sessionRef.current,
        isVisible: false,
      };
  };

  const onAvailableHover = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    available: AvailablePeriod,
    session: { start: Date; end: Date }
  ) => {
    e.stopPropagation();

    const status = [...available.status];
    const statIdx = status.findIndex((stat) => stat === mode);
    const hasMode = statIdx !== -1;

    const userCanUpdate = user?.consultation_duration === user?.consultation_offline_duration;

    if (!userCanUpdate && !hasMode) {
      return (sessionRef.current = {
        ...sessionRef.current,
        isVisible: false,
      });
    }

    const isOverlapping =
      parseISO(available.start_time).getDate() <
      subSeconds(parseISO(available.end_time), 1).getDate();

    const isStart = session.start.getDate() === parseISO(available.start_time).getDate();

    const id = available.start_time;
    const start = document.getElementById(id);
    if (!start) return;
    const coords = start.getBoundingClientRect();

    const fullDuration = getRealDuration();

    const partialDuration = isStart
      ? differenceInMinutes(
          addSeconds(endOfDay(parseISO(available.start_time)), 1),
          parseISO(available.start_time)
        ) / 15
      : differenceInMinutes(parseISO(available.end_time), startOfDay(session.start)) / 15;

    const part = isOverlapping ? (isStart ? 'start' : 'end') : 'full';

    const action = hasMode ? 'remove' : 'update';
    hasMode ? status.splice(statIdx, 1) : status.push(mode);

    sessionRef.current = {
      ...sessionRef.current,
      start: parseISO(available.start_time),
      isVisible: true,
      mode: status,
      action: action,
      duration: isOverlapping ? partialDuration : fullDuration,
      x: isOverlapping ? e.currentTarget.offsetLeft : coords.x,
      y: isStart ? coords.y : e.currentTarget.offsetTop,
      part: part,
    };
  };

  const hide = () => (sessionRef.current = { ...sessionRef.current, isVisible: false });

  return { onEmptyHover, onAvailableHover, hide, sessionRef, calendarRef };
};

export const useSessionCreate = (term?: ScheduleTerm) => {
  // const { user } = useUser();
  const { availablePeriods } = useAvailablePeriods();
  const { tz } = useTz();
  const termDuration = getScheduleServiceDuration(term?.services) || 0;
  const mode = term?.status;

  const defaultParams = {
    x: 0,
    y: 0,
    width: 0,
    start: new Date(),
    mode: mode,
    duration: termDuration,
    isVisible: false,
    action: 'create',
    part: 'full',
  };

  const sessionRef = useRef<{
    x: number;
    y: number;
    width: number;
    start: Date;
    mode: 'ON' | 'OF' | undefined;
    duration: number;
    isVisible: boolean;
    action: string;
    part: string;
    isOverlapping?: boolean;
  }>(defaultParams);

  const calendarRef = useRef<HTMLDivElement>(null);

  // const getRealDuration = useCallback(() => {
  //   return duration;
  // }, [mode, user?.consultation_duration, user?.consultation_offline_duration]);

  useEffect(() => {
    const updateParams = () => {
      sessionRef.current = {
        ...sessionRef.current,
        duration: termDuration,
        mode: mode,
      };
    };

    updateParams();
  }, [term]);

  const onEmptyHover = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    session: { start: Date; end: Date }
    // step: number // - шаг по 5 минут
  ) => {
    // session.start.setMinutes(session.start.getMinutes() + step * 5);
    // console.log("session:",session)
    // console.log("step",step)
    // if (!calendarRef.current) return;
    const realDuration = termDuration || 15;
    // const startReminder = doCalc15MinutesReminder(session.start);
    // if (!term) return;
    // if(termDuration)
    const end = addMinutes(session.start, realDuration);
    const isIntersecting = !!availablePeriods.find(
      (available) =>
        (parseISO(available.start_time) >= zonedTimeToUtc(session.start, tz) &&
          parseISO(available.end_time) <= zonedTimeToUtc(end, tz)) ||
        (parseISO(available.start_time) < zonedTimeToUtc(end, tz) &&
          zonedTimeToUtc(end, tz) <= parseISO(available.end_time))
    );

    const isOverlapping = session.start.getDate() < subSeconds(end, 1).getDate();

    const partialDuration = differenceInMinutes(
      addSeconds(endOfDay(subSeconds(session.end, 1)), 1),
      session.start
    );

    // const childRect = e.currentTarget.getBoundingClientRect();
    // const parentRect = e.currentTarget.parentElement?.getBoundingClientRect();
    // const scrollTop = e.currentTarget.parentElement?.scrollTop;
    // let coords = { x: childRect.x, y: childRect.y };
    //
    // if(parentRect && scrollTop) {
    //   const offsetTop = childRect.top - parentRect.top;
    //   const scrollTop = childRect.top - parentRect.top;
    //   coords = { x: childRect.x, y: childRect.top - parentRect.top + scrollTop - 179.5 };
    // }

    // parentRect &&
    //   (coords = {
    //     x: childRect.left - parentRect.left,
    //     y: childRect.top - parentRect.top,
    //   });
    const coords = e.currentTarget.getBoundingClientRect();
    if (!isIntersecting)
      sessionRef.current = {
        ...sessionRef.current,
        start: session.start,
        mode: mode,
        isVisible: true,
        action: 'create',
        duration: isOverlapping ? partialDuration : realDuration,
        part: isOverlapping ? 'start' : 'full',
        x: coords.x - 1,
        y: coords.y - 1,
        width: coords.width + 2,
        isOverlapping,
      };

    if (isIntersecting)
      sessionRef.current = {
        ...sessionRef.current,
        isVisible: false,
      };
  };

  const onAvailableHover = (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    available: AvailablePeriod,
    session: { start: Date; end: Date }
  ) => {
    e.stopPropagation();
    console.log('AVAILABLE', available.id);
    const status = available.terms[0].status;
    // const statIdx = status.findIndex((stat) => stat === mode);
    // const hasMode = status === mode;
    const availablePeriodDuration = getScheduleServiceDuration(available.terms[0].services);
    const parsedAvailableStart = parseISO(available.start_time);
    const availableStartDate = parsedAvailableStart.getDate();
    // const userCanUpdate = availablePeriodDuration === termDuration;

    // if (!userCanUpdate && !hasMode) {
    //   return (sessionRef.current = {
    //     ...sessionRef.current,
    //     isVisible: false,
    //   });
    // }

    const isOverlapping =
      availableStartDate < subSeconds(parseISO(available.end_time), 1).getDate();

    const isStart = session.start.getDate() <= availableStartDate;

    const id = available.start_time;
    const start = document.getElementById(id);
    const startOverlap = document.getElementById(`${id}_topOverlap`);

    if (!start) return;

    // const coords = e.currentTarget.getBoundingClientRect();

    const fullDuration = availablePeriodDuration || 15;

    const partialDurationIfStart = differenceInMinutes(
      addSeconds(endOfDay(parsedAvailableStart), 1),
      parsedAvailableStart
    );

    const partialDuration = isStart
      ? partialDurationIfStart <= 15
        ? 15
        : partialDurationIfStart
      : differenceInMinutes(parseISO(available.end_time), startOfDay(session.start));

    const part = isOverlapping ? (isStart ? 'start' : 'end') : 'full';

    // const action = hasMode ? 'remove' : 'update';
    // const action = userCanUpdate ? 'update' : 'remove';
    const action = 'remove';
    // hasMode ? status.splice(statIdx, 1) : status.push(mode);

    const finalDuration = isOverlapping ? partialDuration : fullDuration;

    const coords = start.getBoundingClientRect();
    const coordsOverlap = startOverlap?.getBoundingClientRect();

    const cordsEl = e.currentTarget.getBoundingClientRect();

    const finalYCoord = part === 'end' && coordsOverlap ? coordsOverlap.y : coords.y;

    sessionRef.current = {
      ...sessionRef.current,
      start: parsedAvailableStart,
      isVisible: true,
      mode: mode,
      // action: action,
      action: 'remove',
      duration: finalDuration,
      x: cordsEl.x,
      y: finalYCoord,
      width: coords.width,
      part: part,
      isOverlapping,
    };
  };

  const hide = () =>
    // console.log('termDuration:',termDuration);
    (sessionRef.current = {
      ...sessionRef.current,
      // duration: termDuration || 15,
      isVisible: false,
    });

  return { onEmptyHover, onAvailableHover, hide, sessionRef, calendarRef };
};
